import { RegisteredPtInviteData, UnregisteredPtInviteData } from "@domain/healthcare-team/healthcare-team";
import { Patient } from "@domain/patient/patient";
import { User } from "@domain/user/user";
import { EmailFormDialog } from "@ui/pages/patient-details/components/healthcare-team-tab/dialog/steps/healthcare-team-email-step";
import { InviteRegisteredPtDialog } from "@ui/pages/patient-details/components/healthcare-team-tab/dialog/steps/healthcare-team-invite-registered-pt-step";
import { InviteUnregisteredPtDialog } from "@ui/pages/patient-details/components/healthcare-team-tab/dialog/steps/healthcare-team-invite-unregistered-pt-step";
import { DialogProps } from "primereact/dialog";
import React, { useCallback, useState } from "react";

export interface HealthcareTeamInviteDialogProps extends DialogProps {
	patient: Patient;
	invitingPt: User;
}

enum InvitationStep {
	EmailForm = "email-form",
	InviteRegistered = "invite-registered",
	InviteUnregistered = "invite-unregistered",
}

type EmailFormStep = {
	step: InvitationStep.EmailForm;
	defaultValues?: { email: string };
};

type InviteRegisteredStep = {
	step: InvitationStep.InviteRegistered;
	ptData: RegisteredPtInviteData;
};

type InviteUnregisteredStep = {
	step: InvitationStep.InviteUnregistered;
	ptData: UnregisteredPtInviteData;
};

type DialogStep = EmailFormStep | InviteRegisteredStep | InviteUnregisteredStep;

export const HealthcareTeamInviteDialog = ({
	invitingPt,
	...otherProps
}: HealthcareTeamInviteDialogProps): JSX.Element => {
	const [dialogStep, setDialogStep] = useState<DialogStep>({ step: InvitationStep.EmailForm });

	const onInviteFormGoBack = useCallback(
		(email: string) => setDialogStep({ step: InvitationStep.EmailForm, defaultValues: { email } }),
		[],
	);

	if (dialogStep.step === InvitationStep.EmailForm) {
		return (
			<EmailFormDialog
				{...otherProps}
				onRegisteredPt={ptData => setDialogStep({ step: InvitationStep.InviteRegistered, ptData })}
				onUnregisteredPt={ptData => setDialogStep({ step: InvitationStep.InviteUnregistered, ptData })}
				defaultValues={dialogStep.defaultValues}
			/>
		);
	}

	if (dialogStep.step === InvitationStep.InviteRegistered) {
		return (
			<InviteRegisteredPtDialog
				{...otherProps}
				invitedPt={dialogStep.ptData}
				invitingPt={invitingPt}
				onInvitationSent={otherProps.onHide}
				onGoBack={() => onInviteFormGoBack(dialogStep.ptData.email)}
			/>
		);
	}

	return <InviteUnregisteredPtDialog {...otherProps} invitedPt={dialogStep.ptData} invitingPt={invitingPt} />;
};
